import { Button, Checkbox } from '@pms-digital/design-system';
import { useEffect, useId, useRef, useState } from 'react';

export interface MultiSelectFilterOption<T extends string> {
  value: T;
  label: string;
}

export interface MultiSelectFilterProps<T extends string> {
  /** « État », « Pivot », « Destinataire ». */
  label: string;
  options: readonly MultiSelectFilterOption<T>[];
  selected: readonly T[];
  onChange: (selected: T[]) => void;
}

/**
 * A multi-choice filter of the toolbars (planche 03): a button, its label
 * followed by the count of checked values once active, and a panel of
 * `Checkbox`es that stays open while values are toggled; Échap or a click
 * outside closes it. The selection keeps the options' order, whatever the
 * click order.
 */
export function MultiSelectFilter<T extends string>({
  label,
  options,
  selected,
  onChange,
}: MultiSelectFilterProps<T>) {
  const active = selected.length > 0;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function toggle(value: T) {
    const next = selected.includes(value)
      ? selected.filter((entry) => entry !== value)
      : [...selected, value];
    onChange(options.map((option) => option.value).filter((entry) => next.includes(entry)));
  }

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant={active ? 'secondary' : 'field'}
        size="sm"
        compact
        iconAfter="expand_more"
        aria-label={active ? `${label} (${selected.length} sélectionnés)` : label}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((previous) => !previous)}
      >
        {active ? `${label} · ${selected.length}` : label}
      </Button>
      {open && (
        <fieldset
          id={panelId}
          className="absolute top-full left-0 z-20 m-0 mt-1 flex min-w-60 flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 shadow-md"
        >
          <legend className="sr-only">{label}</legend>
          {options.map((option) => (
            <Checkbox
              key={option.value}
              label={option.label}
              checked={selected.includes(option.value)}
              onCheckedChange={() => toggle(option.value)}
            />
          ))}
        </fieldset>
      )}
    </div>
  );
}
